import { useEffect, useRef } from 'react';
import type { StakeReport, StakeScenarioRow, StakeScenarioWinType } from '@sr-test-wizard/shared';
import { useStakeScenariosStore } from '../../state/stakeScenariosStore';
import { useStakeEngineStore } from '../../state/stakeEngineStore';
import { ScenarioDetail } from './ScenarioDetail';

const WIN_TYPES: StakeScenarioWinType[] = ['dead', 'sub', 'win', 'wincap'];

function winTypeClass(w: StakeScenarioWinType): string {
  if (w === 'wincap') return 'bad';
  if (w === 'win') return 'good';
  if (w === 'sub') return 'warn';
  return 'muted';
}

function oddsLabel(oneIn: number | null): string {
  if (oneIn == null || !Number.isFinite(oneIn) || oneIn <= 0) return '—';
  if (oneIn < 10) return `1 in ${oneIn.toFixed(1)}`;
  return `1 in ${Math.round(oneIn).toLocaleString()}`;
}

/** Short game-agnostic feature badges for a row, from the mode's feature labels. */
function featureBadges(row: StakeScenarioRow, labels: Map<string, string>): string {
  return row.features.map((k) => labels.get(k) ?? k).join(', ');
}

function parseProgress(
  tool: string | undefined,
  kind: 'explain-shapes' | 'explain-rounds',
): { label: string; pct: number | null } | null {
  if (!tool) return null;
  const m = new RegExp(`${kind}\\s+(\\d+)\\/(\\d+)`).exec(tool);
  if (!m) return null;
  const done = Number(m[1]);
  const total = Number(m[2]);
  const pct = total > 0 ? Math.round((done / total) * 100) : null;
  const noun = kind === 'explain-shapes' ? 'shapes' : 'rounds';
  return { label: `Explaining ${noun} ${done.toLocaleString()}/${total.toLocaleString()}`, pct };
}

function ProgressBlock({ label, pct, note }: { label: string; pct: number | null; note?: string }) {
  return (
    <div className="scn-progress">
      <div className="scn-progress-row">
        <span className="spinner" />
        <span className="small">{label}</span>
        {pct != null && <span className="small muted">{pct}%</span>}
      </div>
      {pct != null && (
        <div className="scn-progress-bar">
          <div className="scn-progress-fill" style={{ width: `${pct}%` }} />
        </div>
      )}
      {note && <p className="small muted" style={{ margin: '4px 0 0' }}>{note}</p>}
    </div>
  );
}

export function ScenariosTab({ runId, report }: { runId: string; report: StakeReport | null }) {
  const s = useStakeScenariosStore();
  const scnProgress = useStakeEngineStore((st) => st.progress.scenarios);
  const modes = report?.math.rows.map((r) => r.mode) ?? [];

  useEffect(() => {
    s.init(runId);
  }, [runId, s]);

  // Auto-select the first mode once modes are known and nothing is selected.
  useEffect(() => {
    if (!s.mode && modes.length) void s.selectMode(modes[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modes.join(','), s.mode]);

  if (!report || report.status === 'idle' || modes.length === 0) {
    return (
      <section className="card">
        <p className="muted">Run a verification first (RTP analysis tab) so the modes and book files are known.</p>
      </section>
    );
  }

  const totalPages = Math.max(1, Math.ceil(s.total / s.pageSize));

  const pageIds = s.rows.map((r) => r.id);
  const selectedOnPage = pageIds.filter((id) => s.selectedIds.has(id)).length;
  const pageAllSelected = pageIds.length > 0 && selectedOnPage === pageIds.length;
  const pageCheckRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (pageCheckRef.current) pageCheckRef.current.indeterminate = selectedOnPage > 0 && !pageAllSelected;
  }, [selectedOnPage, pageAllSelected]);

  // Game-agnostic columns: discovered categorical attributes + a feature-label
  // lookup, both derived from the mode profile (empty for games without them).
  const attrDefs = s.meta?.facets.attributes ?? [];
  const labelMap = new Map(Object.entries(s.meta?.profile.labels ?? {}));
  const colCount = 9 + attrDefs.length;

  return (
    <section className="card">
      <div className="row" style={{ alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Scenario explorer</h2>
        <span className="small muted">every book record per math, filterable</span>
        <span className="spacer" />
        <label className="small muted">mode&nbsp;</label>
        <select value={s.mode ?? ''} onChange={(e) => void s.selectMode(e.target.value)}>
          {modes.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>

      {s.error && <p className="small" style={{ color: 'var(--bad)' }}>{s.error}</p>}

      {s.metaLoading ? (
        <p className="muted small" style={{ marginTop: 12 }}>Loading…</p>
      ) : !s.meta ? (
        <div style={{ marginTop: 12 }}>
          <p className="muted small">
            No scenario index built for <strong>{s.mode}</strong> yet. Building decompresses the book
            and classifies every record (~3s for 100k).
          </p>
          <button type="button" className="btn btn-primary" disabled={s.building} onClick={() => void s.build()}>
            {s.building ? 'Building…' : 'Build scenario index'}
          </button>
        </div>
      ) : (
        <>
          <div className="scn-summary small muted" style={{ marginTop: 8 }}>
            {s.meta.totalRecords.toLocaleString()} records · {s.meta.shapes.length} distinct shapes · max{' '}
            {s.meta.facets.maxMultiplier.toLocaleString()}x
            <span className="spacer" />
            <button
              type="button"
              className="btn small"
              disabled={s.explainingShapes}
              onClick={() => void s.explainShapes()}
              title="One short AI call per distinct shape (cheap); covers all records"
            >
              {s.explainingShapes ? 'Explaining shapes…' : s.shapesGenerated ? 'Regenerate shape AI' : 'Explain shapes with AI'}
            </button>
            <button type="button" className="btn small" disabled={s.building} onClick={() => void s.build()} style={{ marginLeft: 8 }}>
              {s.building ? 'Rebuilding…' : 'Rebuild'}
            </button>
          </div>

          {s.explainingShapes && (() => {
            const p = parseProgress(scnProgress?.tool, 'explain-shapes');
            return (
              <ProgressBlock
                label={p ? p.label : 'Starting shape analysis…'}
                pct={p?.pct ?? null}
                note={`One short AI call per distinct round-shape — covers all ${s.meta.totalRecords.toLocaleString()} records.`}
              />
            );
          })()}

          <FilterBar />

          <BulkBar scnTool={scnProgress?.tool} />

          <div className="row small muted" style={{ alignItems: 'center', marginTop: 8 }}>
            <span>{s.total.toLocaleString()} matching</span>
            <span className="spacer" />
            <label>sort&nbsp;</label>
            <select value={s.sort} onChange={(e) => s.setSort(e.target.value as never)}>
              <option value="idAsc">id ↑</option>
              <option value="multDesc">multiplier ↓</option>
              <option value="multAsc">multiplier ↑</option>
              <option value="oddsAsc">rarest first</option>
              <option value="weightDesc">most common</option>
            </select>
          </div>

          <div style={{ overflowX: 'auto', marginTop: 8 }}>
            <table className="data-table scn-table">
              <thead>
                <tr>
                  <th style={{ width: 28 }}>
                    <input
                      type="checkbox"
                      ref={pageCheckRef}
                      checked={pageAllSelected}
                      onChange={(e) => s.toggleSelectPage(e.target.checked)}
                      title="Select/deselect this page"
                    />
                  </th>
                  <th>id</th>
                  <th>criteria</th>
                  <th>multiplier</th>
                  <th>odds</th>
                  <th>outcome</th>
                  <th>features</th>
                  {attrDefs.map((a) => <th key={a.key}>{a.label.toLowerCase()}</th>)}
                  <th>wins</th>
                  <th title="Has a bespoke AI explanation">AI</th>
                </tr>
              </thead>
              <tbody>
                {s.rowsLoading ? (
                  <tr><td colSpan={colCount} className="muted small">Loading…</td></tr>
                ) : s.rows.length === 0 ? (
                  <tr><td colSpan={colCount} className="muted small">No records match these filters.</td></tr>
                ) : (
                  s.rows.map((r) => (
                    <tr
                      key={r.id}
                      className={`row-clickable${s.selectedIds.has(r.id) ? ' row-selected' : ''}`}
                      onClick={() => void s.openRecord(r.id)}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={s.selectedIds.has(r.id)}
                          onChange={() => s.toggleSelect(r.id)}
                        />
                      </td>
                      <td className="mono">{r.id}</td>
                      <td className="mono">{r.criteria}</td>
                      <td className="mono">{r.multiplier.toLocaleString()}x</td>
                      <td className="small">{oddsLabel(r.oneIn)}</td>
                      <td><span className={`chip ${winTypeClass(r.winType)}`}>{r.winType}</span></td>
                      <td className="small">{featureBadges(r, labelMap) || '—'}</td>
                      {attrDefs.map((a) => <td key={a.key} className="small">{r.attrs[a.key] ?? '—'}</td>)}
                      <td className="small">{r.numWins || ''}</td>
                      <td className="small" style={{ textAlign: 'center' }}>{s.explainedIds.has(r.id) ? '✓' : ''}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="row" style={{ alignItems: 'center', marginTop: 8, gap: 8 }}>
            <button type="button" className="btn small" disabled={s.page <= 0} onClick={() => s.setPage(s.page - 1)}>
              ‹ Prev
            </button>
            <span className="small muted">page {s.page + 1} / {totalPages}</span>
            <button
              type="button"
              className="btn small"
              disabled={s.page >= totalPages - 1}
              onClick={() => s.setPage(s.page + 1)}
            >
              Next ›
            </button>
          </div>
        </>
      )}

      {(s.detail || s.detailLoading) && <ScenarioDetail />}
    </section>
  );
}

function BulkBar({ scnTool }: { scnTool: string | undefined }) {
  const s = useStakeScenariosStore();
  const n = s.selectedIds.size;
  const bulk = s.bulkExplaining ? parseProgress(scnTool, 'explain-rounds') : null;
  return (
    <div className="scn-bulkbar">
      <span className="small muted" style={{ flexBasis: '100%' }}>
        Tip: "Explain shapes with AI" already covers every round instantly. Use the per-round AI below
        only when you want unique wording for a specific subset.
      </span>
      <span className="small"><strong>{n.toLocaleString()}</strong> selected</span>
      <button type="button" className="btn small" disabled={s.selectingAll} onClick={() => void s.selectAllFiltered()}>
        {s.selectingAll ? 'Selecting…' : `Select all filtered (${s.total.toLocaleString()})`}
      </button>
      <button type="button" className="btn small" disabled={n === 0} onClick={() => s.clearSelection()}>
        Clear
      </button>
      <span className="spacer" />
      {n > 500 && !s.bulkExplaining && (
        <span className="small" style={{ color: 'var(--warn, #b45309)' }}>
          Large batch — slower &amp; costs more
        </span>
      )}
      {s.bulkExplaining ? (
        <button
          type="button"
          className="btn small"
          style={{ color: 'var(--bad)', borderColor: 'var(--bad)' }}
          onClick={() => void s.stopExplain()}
          title="Stop after the current in-flight batches; everything explained so far is kept"
        >
          Stop
        </button>
      ) : (
        <button
          type="button"
          className="btn btn-primary small"
          disabled={n === 0}
          onClick={() => {
            if (n > 200000) {
              window.alert(`Too many rounds selected (${n.toLocaleString()}). The limit is 200,000 per run — filter further first.`);
              return;
            }
            if (n > 2000 && !window.confirm(`Explain ${n.toLocaleString()} rounds with AI? Rounds are batched and run in parallel, but this still takes time and tokens for large sets.`)) {
              return;
            }
            void s.explainSelected();
          }}
          title="Bespoke per-round AI explanations for the checked rows"
        >
          {`Explain selected with AI (${n.toLocaleString()})`}
        </button>
      )}
      {s.bulkExplaining && (
        <div style={{ flexBasis: '100%' }}>
          <ProgressBlock label={bulk ? bulk.label : 'Starting round analysis…'} pct={bulk?.pct ?? null} />
        </div>
      )}
    </div>
  );
}

function FilterBar() {
  const s = useStakeScenariosStore();
  const facets = s.meta?.facets;
  if (!facets) return null;
  const f = s.filters;

  const toggleWin = (w: StakeScenarioWinType) => {
    const cur = new Set(f.winType ?? []);
    if (cur.has(w)) cur.delete(w);
    else cur.add(w);
    s.setFilters({ winType: [...cur] });
  };
  const toggleFeature = (key: string) => {
    const cur = new Set(f.features ?? []);
    if (cur.has(key)) cur.delete(key);
    else cur.add(key);
    s.setFilters({ features: [...cur] });
  };
  const setAttr = (key: string, value: string) => {
    const next = { ...(f.attrs ?? {}) };
    if (value) next[key] = value;
    else delete next[key];
    s.setFilters({ attrs: next });
  };

  return (
    <div className="scn-filter-bar">
      <div className="scn-filter-group">
        <span className="scn-filter-label">outcome</span>
        {WIN_TYPES.map((w) => (
          <button
            key={w}
            type="button"
            className={`chip toggle ${(f.winType ?? []).includes(w) ? winTypeClass(w) + ' on' : 'muted'}`}
            onClick={() => toggleWin(w)}
          >
            {w}
          </button>
        ))}
      </div>
      <div className="scn-filter-group">
        <span className="scn-filter-label">AI analysis</span>
        <button
          type="button"
          className={`chip toggle ${f.explained === 'yes' ? 'good on' : 'muted'}`}
          onClick={() => s.setFilters({ explained: f.explained === 'yes' ? null : 'yes' })}
          title="Only rounds that already have a bespoke AI explanation"
        >
          has AI
        </button>
        <button
          type="button"
          className={`chip toggle ${f.explained === 'no' ? 'warn on' : 'muted'}`}
          onClick={() => s.setFilters({ explained: f.explained === 'no' ? null : 'no' })}
          title="Only rounds without a bespoke AI explanation yet"
        >
          no AI
        </button>
      </div>
      {facets.features.length > 0 && (
        <div className="scn-filter-group">
          <span className="scn-filter-label">features</span>
          {facets.features.map((ft) => (
            <button
              key={ft.key}
              type="button"
              className={`chip toggle ${(f.features ?? []).includes(ft.key) ? 'good on' : 'muted'}`}
              onClick={() => toggleFeature(ft.key)}
              title={`${ft.count.toLocaleString()} rounds`}
            >
              {ft.label}
            </button>
          ))}
        </div>
      )}
      <div className="scn-filter-group">
        <span className="scn-filter-label">multiplier</span>
        <input
          type="number"
          placeholder="min"
          style={{ width: 70 }}
          value={f.multMin ?? ''}
          onChange={(e) => s.setFilters({ multMin: e.target.value === '' ? null : Number(e.target.value) })}
        />
        <span className="muted">–</span>
        <input
          type="number"
          placeholder="max"
          style={{ width: 70 }}
          value={f.multMax ?? ''}
          onChange={(e) => s.setFilters({ multMax: e.target.value === '' ? null : Number(e.target.value) })}
        />
      </div>
      {facets.attributes.map((attr) => (
        <div className="scn-filter-group" key={attr.key}>
          <span className="scn-filter-label">{attr.label.toLowerCase()}</span>
          <select value={f.attrs?.[attr.key] ?? ''} onChange={(e) => setAttr(attr.key, e.target.value)}>
            <option value="">any</option>
            {attr.values.map((v) => (
              <option key={v.value} value={v.value}>{v.value} ({v.count})</option>
            ))}
          </select>
        </div>
      ))}
      <div className="scn-filter-group">
        <span className="scn-filter-label">criteria</span>
        <select value={f.criteria ?? ''} onChange={(e) => s.setFilters({ criteria: e.target.value || null })}>
          <option value="">any</option>
          {facets.criteria.map((c) => (
            <option key={c.value} value={c.value}>{c.value} ({c.count})</option>
          ))}
        </select>
      </div>
      <div className="scn-filter-group">
        <span className="scn-filter-label">event</span>
        <select value={f.eventType ?? ''} onChange={(e) => s.setFilters({ eventType: e.target.value || null })}>
          <option value="">any</option>
          {facets.eventTypes.map((c) => (
            <option key={c.value} value={c.value}>{c.value}</option>
          ))}
        </select>
      </div>
      <div className="scn-filter-group">
        <span className="scn-filter-label">id</span>
        <input
          type="number"
          placeholder="#"
          style={{ width: 90 }}
          value={f.id ?? ''}
          onChange={(e) => s.setFilters({ id: e.target.value === '' ? null : Number(e.target.value) })}
        />
      </div>
      <button type="button" className="btn small" onClick={() => s.clearFilters()}>Clear</button>
    </div>
  );
}
